import React, { useState, useRef, useEffect } from 'react';
import { inspect } from 'util'
import { useTranslation } from 'react-i18next'
import './toggle-switch.css'

import { uniqueId } from 'lodash'

export interface ToggleSwitchProps extends React.HTMLProps<HTMLInputElement> {
  controlled?: boolean
};

export function ToggleSwitch({ id, label, disabled = false, checked, onChange, controlled, ...otherProps }: ToggleSwitchProps) {
  const defaultSwitched = !!checked;
  const [isSwitched, setIsSwitched] = useState(defaultSwitched)
  const [isFocused, setIsFocused] = useState(false)
  const toggleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (controlled) {
      setIsSwitched(checked)
    }
  }, [checked])

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
  }

  const altId = useRef(`toggle-switch-${uniqueId()}`)

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.code === 'Space' || event.code === 'Enter') && toggleRef.current && !disabled) {
      toggleRef.current.click()
    }
  }

  return (
    <>
      <input
        {...otherProps}
        id={id || altId.current}
        data-testid={id}
        type="checkbox"
        checked={isSwitched}
        disabled={disabled}
        onChange={(...args) => {
          args[0].stopPropagation()
          if (!controlled) {
            setIsSwitched((prev) => !prev)
          }
          if (onChange) { onChange(...args) }
        }}
        className="toggle-switch"
        ref={toggleRef}
      />
      <label
        className={`toggle-switch-label ${disabled ? 'disabled' : ''} ${isSwitched ? 'switched-on' : ''}`}
        htmlFor={id || altId.current}
        data-testid={`toggle-label-${id}`}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <span className={`toggle-switch-button ${isFocused ? 'focused' : ''} ${isSwitched ? 'switched-on' : ''}`} tabIndex={0} onKeyUp={handleKeyUp} id={`toggle-span-${id}`} data-testid={`toggle-span-${id}`} />
      </label>
    </>
  );
}